import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, skipWhile, Subscription } from 'rxjs';
import { catchError, filter, finalize, take, tap } from 'rxjs/operators';
import { sKey } from '@easy-lib/modules/shared/services/local-storage.service';
import { SymbolService } from '@easy-lib/modules/shared/services/symbol.service';
import { TrackerService, TrackKey } from '@easy-lib/modules/shared/services/tracker.service';
import { LsService } from '@easy-lib/modules/shared/services/Ls.service';

import { UserDeviceService } from '@easy-lib/modules/shared/services/user-device.service';

import { MarketDataService } from '@easy-lib/modules/shared/services/market-data.service';
import { DeviceTypeEnum } from '@easy-lib/modules/account-activity/models/accountDto';
import {
	defaultMarketData,
	ISymbol,
	ISymbolData,
	ISymbolMarketData
} from '@easy-lib/modules/shared/models/symbol.model';
import { activeStockLsData } from '@easy-lib/tools/ls-tool';
import { getColorOfSymbolState } from '@easy-lib/tools/symbol-state.tool';
import { LsSchemaType } from '@easy-lib/modules/shared/models/ls.model';
import { EASY_CHART_LAYOUT } from '@easy-lib/constants';

export type Layout = { symbols: Record<number, string>; layout: any; details: boolean; list: string };
@Injectable({
	providedIn: 'root'
})
export class DesktopActiveSymbolService implements OnDestroy {
	public stockIsinMapper: { [isin: string]: { name: string; title: string } } = {};
	changeActiveIsinEventRaised = false;

	private defaultIsin: string = localStorage.getItem(sKey.defaultIsin) || 'IRT3SATF0001';
	private activeSymbolSub = new BehaviorSubject<ISymbolData>(null);
	public activeSymbolObs$ = this.activeSymbolSub.asObservable();

	private activeSymbolIsin: string;
	private activeSymbolLsSubId: string;
	private subs: Subscription[] = [];

	constructor(
		private readonly lsService: LsService,
		private readonly symbolService: SymbolService,
		private readonly trackerService: TrackerService,
		private readonly deviceService: UserDeviceService,
		private readonly marketDataService: MarketDataService
	) {
		this.setDefaultIsinAtStartup();
		this.mapStockIsinsToNames();

		this.lsService.resetLsManually$.pipe(filter(res => res)).subscribe(() => {
			this.subscribeToActiveSymbol();
		});
	}

	public get activeStockIsin(): string {
		return this.activeSymbolIsin;
	}

	public set activeStockIsin(value: string) {
		if (value === this.activeSymbolIsin) return;

		if (this.activeSymbolIsin) {
			this.trackerService.endTimer(TrackKey.symbol, this.activeSymbolIsin);
			this.unsubscribeBySymbolIsinChange().then(() => {
				this.getMarketData(value);
			});
		} else {
			this.getMarketData(value);
		}
	}
	getMarketData(isin: string) {
		this.activeSymbolIsin = isin;
		this.setSymbolData();
		this.marketDataService.callMarketDataIfLsNotConnected(() => this.getStockDetailsMarketData());
		this.subscribeToActiveSymbol();
	}

	ngOnDestroy(): void {
		this.subs.forEach(s => s.unsubscribe());
	}

	private setDefaultIsinAtStartup(): void {
		const sub = this.deviceService.deviceType$
			.pipe(
				skipWhile(data => data === DeviceTypeEnum.Unknown),
				take(1),
				tap((deviceType: DeviceTypeEnum) => {
					if (deviceType === DeviceTypeEnum.Desktop) {
						this.activeStockIsin = this.defaultIsin;
					}
				}),
				finalize(() => sub.unsubscribe())
			)
			.subscribe();
		this.subs.push(sub);
	}

	private async setSymbolData() {
		const symbol = await this.symbolService.getSymbol(this.activeSymbolIsin.toUpperCase());
		this.activeSymbolSub.next({ symbol, marketData: { ...defaultMarketData } } as ISymbolData);
		this.setTradingViewLayoutActiveIsin(symbol.symbolIsin);
		localStorage.setItem(sKey.defaultIsin, this.activeSymbolIsin);
		this.trackerService.counter(TrackKey.symbol, this.activeSymbolIsin, symbol);
		this.trackerService.startTimer(TrackKey.symbol, this.activeSymbolIsin, symbol);
	}

	setTradingViewLayoutActiveIsin(isin) {
		const tVlayoutObject = localStorage.getItem(EASY_CHART_LAYOUT)
			? JSON.parse(localStorage.getItem(EASY_CHART_LAYOUT))
			: ({
					symbols: { 0: isin },
					layout: 's',
					details: true,
					list: ''
				} as Layout);

		if (tVlayoutObject.layout === 's') {
			tVlayoutObject.symbols[0] = isin;
			localStorage.setItem(EASY_CHART_LAYOUT, JSON.stringify(<Layout>{ ...tVlayoutObject }));
		}
	}

	private subscribeToActiveSymbol(): void {
		this.lsService.subscribeToLs(LsSchemaType.ActiveStock, this.activeSymbolIsin).subscribe(data => {
			if (data.inst === this.activeSymbolIsin) {
				this.marketDataService.lsDataIsReceived(this.getStockDetailsMarketData.name);
				this.activeSymbolLsSubId = data.subId;
				const marketData: ISymbolMarketData = activeStockLsData(data.val);
				marketData.closingPriceVarPercent =
					this.symbolService.calculateClosingPriceVarPercent(
						marketData.closingPrice,
						marketData.feeOfPreviousDaysClosingPrice,
						marketData.totalNumberOfSharesTraded
					) || 0;
				const symbol: ISymbol = this.activeSymbolSub.getValue()?.symbol;
				this.activeSymbolSub.next({ symbol, marketData } as ISymbolData);
			}
		});
	}

	getStockDetailsMarketData(): void {
		const sub = this.marketDataService
			.GetSymbolDetailsHeader(this.activeStockIsin)
			.pipe(
				tap(data => {
					const subData = this.activeSymbolSub.getValue();
					const subSymbol: ISymbol = this.activeSymbolSub.getValue()?.symbol;
					const symbol = subData.symbol || subSymbol;
					const marketData = subData.marketData ?? defaultMarketData;
					marketData.lastTradedPrice = data.lastTradedPrice;
					marketData.feeOfPreviousDaysClosingPrice = data.feeOfPreviousDaysClosingPrice;
					marketData.lastTradedPriceVar = this.symbolService.calculateLastTradedPriceVar(
						data.lastTradedPrice,
						data.feeOfPreviousDaysClosingPrice
					);
					marketData.lastTradedPriceVarPercent = data.priceVar;
					marketData.stateColor = getColorOfSymbolState(data.stateCode);
					marketData.closingPrice = data.closingPrice;
					marketData.closingPriceVarPercent = this.symbolService.calculateClosingPriceVarPercent(
						data.closingPrice,
						data.feeOfPreviousDaysClosingPrice,
						data.totalNumberOfSharesTraded
					);
					marketData.totalNumberOfSharesTraded = data.totalNumberOfSharesTraded;
					marketData.stateCode = data.stateCode;

					this.activeSymbolSub.next({ symbol, marketData } as ISymbolData);
				}),
				catchError((error, caught) => caught),
				finalize(() => sub.unsubscribe())
			)
			.subscribe();
	}

	async unsubscribeBySymbolIsinChange() {
		this.lsService.unsubscribeSchemaById(this.activeSymbolLsSubId);
		this.subs.forEach(s => s.unsubscribe());
	}

	setActiveIsin = (symbolIsin: string, { openPopup = true } = {}) => {
		this.changeActiveIsinEventRaised = openPopup;
		this.activeStockIsin = symbolIsin;
	};

	async mapStockIsinsToNames() {
		const symbols = await this.symbolService.getSymbols();

		for (let i = 0; i < symbols.length; i++) {
			const symbol = symbols[i];
			this.stockIsinMapper[symbol.symbolIsin] = {
				name: symbol.symbolName,
				title: symbol.title
			};
		}
	}

	callTrackerService() {
		if (this.activeSymbolIsin) this.trackerService.endTimer(TrackKey.symbol, this.activeSymbolIsin);
	}
}
